// @ts-check
import { Flex, Padding } from 'components/base/layout-components';
import ToggleBox from 'components/ToggleBox';
import Text from 'components/base/Text';
import Icon from 'components/base/Icon';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { repositoriesByIdSelector } from 'state/selectors/repositories';
import ServiceMetricsChart from './ServiceMetricsChart';
import { LBButton } from '@linearb/buzz';

const Separator = () => <div className='text-sm mx-2'>|</div>;

const OptionsButton = ({ icon, onClick }) => {
  return (
    <LBButton
      variant='ghost'
      size='icon'
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      data-cy={`options-icon-${icon}`}
    >
      <Icon name={icon} size='small' />
    </LBButton>
  );
};

const ServiceRowContent = ({ service, onEdit, onDelete, readonly }) => {
  const { name } = service;
  const repos = _.get(service, 'settings.path_strategy', []);
  const foldersLength = repos.reduce((acc, repo) => {
    const folders = _.get(repo, 'paths');
    return acc + folders.length;
  }, 0);

  const reposStr = repos.length > 1 ? 'repositories' : 'repository';
  const foldersStr = foldersLength > 1 ? 'folders' : 'folder';

  return (
    <div className='flex flex-row items-center w-full'>
      <div data-cy='service-title' className='text-sm whitespace-nowrap'>
        {name}
      </div>
      <Separator />
      <div className='flex flex-row items-center'>
        <div className='text-sm whitespace-nowrap mr-2'>{`${repos.length} ${reposStr}`}</div>
        <div className='text-sm whitespace-nowrap mr-2'>{`${foldersLength} ${foldersStr}`}</div>
      </div>
      <div className='flex flex-row gap-2 w-full items-center justify-end'>
        <div className='w-40 h-8'>
          <ServiceMetricsChart service={service} />
        </div>
        {!readonly && <OptionsButton icon='pencil' onClick={onEdit} />}
        {!readonly && <OptionsButton icon='Delete' onClick={onDelete} />}
      </div>
    </div>
  );
};

const ServiceRow = ({
  service,
  onEdit,
  onDelete,
  isChecked,
  onCheck,
  readonly
}) => {
  const repositoriesKeys = useSelector(repositoriesByIdSelector);
  const pathStrategies = _.get(service, 'settings.path_strategy', []);

  return (
    <Padding b='small' data-cy='service-row'>
      <ToggleBox
        collapsed={true}
        notActive={false}
        isChecked={isChecked}
        onCheck={onCheck}
        title={
          <ServiceRowContent
            service={service}
            onEdit={onEdit}
            onDelete={onDelete}
            readonly={readonly}
          />
        }
      >
        <Flex direction='column'>
          {pathStrategies.map(({ paths, repository_id }) => {
            const repo = _.get(repositoriesKeys, repository_id);
            const repoName = _.get(repo, 'name');
            return paths.map(path => (
              <Text
                key={`${repository_id}-${path}`}
                padding='1rem'
                ml='2.7rem'
                color='shuttleGray'
                size='medium'
              >
                {repoName}/{path}
              </Text>
            ));
          })}
        </Flex>
      </ToggleBox>
    </Padding>
  );
};

export default ServiceRow;
